import React, { useState, useEffect, useMemo } from "react";
import {
  Card,
  CardBody,
  Col,
  Container,
  Form,
  FormGroup,
  Label,
  Row,
  Button,
  Input,
  Table,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
} from "reactstrap";
import Select from "react-select";
import { ClipLoader } from "react-spinners";
import Swal from "sweetalert2";
import { getCustomers, searchCustomers } from "../../components/Api/GmsCustomer";
import { getEmployees } from "../../components/Api/GmsEmployee";
import { getRooms } from "../../components/Api/GmsRoom";
import { saveBooking, checkAvailability, checkAvailabilityForTime, signBookingConsent } from "../../components/Api/GmsBooking";
import { getSpaServices } from "../../components/Api/GmsSpaService";
import { getSpaPackages, getSpaPackageById } from "../../components/Api/GmsSpaPackage";
import { getSpaPackageSeries } from "../../components/Api/GmsSpaPackageSeries";
import { getCustomerMemberships } from "../../components/Api/GmsCustomerMembership";
import { getEquipments } from "../../components/Api/GmsEquipment";
import { getSources } from "../../components/Api/GmsSource";
import { Calendar, User, X } from "lucide-react";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import Breadcrumb from "../../components/Common/Breadcrumb";
import { useLocation, useNavigate } from "react-router-dom";
import QuickAddCustomer from "../../components/QuickAddCustomer";
import CustomerProfileModal from "../../components/CustomerProfileModal";
import ConsentFormModal from "../../components/ConsentFormModal";
import { 
  hasPagePermissionWithAdmin, 
  hasViewOnlyPermission, 
  hasEditOrHigherPermission,
  isUserAdmin 
} from "../../components/Permission/PermissionService";

// Custom styles for Select components (from PayrollProcessing)
const selectStyles = {
  control: (provided, state) => ({
    ...provided,
    minHeight: '38px',
    borderColor: state.isFocused ? '#556ee6' : '#ced4da',
    boxShadow: state.isFocused ? '0 0 0 0.2rem rgba(85, 110, 230, 0.25)' : 'none',
  }),
  menu: (provided) => ({
    ...provided,
    zIndex: 9999,
    maxHeight: 200,
  }),
  menuList: (provided) => ({
    ...provided,
    maxHeight: 180,
  }),
  option: (provided, state) => ({
    ...provided,
    backgroundColor: state.isSelected ? '#556ee6' : state.isFocused ? '#f8f9fa' : 'white',
    color: state.isSelected ? 'white' : '#495057',
    '&:hover': {
      backgroundColor: state.isSelected ? '#556ee6' : '#e9ecef',
    },
  }),
};

// Session management
const getUserSession = () => {
  const authUser = JSON.parse(localStorage.getItem("authUser") || "{}");
  const user = JSON.parse(localStorage.getItem("user") || "{}");
  return {
    userId: authUser.userId || 1,
    userName: authUser.userName || "System",
    companyId: authUser.companyId || user.user_Company_Id || null,
    branchId: authUser.branchId || user.user_Branch_Id || null,
  };
};

// Helper: format local date/time without timezone
const formatLocalDate = (date) => {
  if (!date) return null;
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
};

const formatLocalDateTime = (date) => {
  if (!date) return null;
  const ymd = formatLocalDate(date);
  const hh = String(date.getHours()).padStart(2, '0');
  const mm = String(date.getMinutes()).padStart(2, '0');
  const ss = String(date.getSeconds()).padStart(2, '0');
  return `${ymd}T${hh}:${mm}:${ss}`;
};

const defaultForm = {
  booking_Id: null,
  customer_Id: null,
  employee_Id: null,
  room_Id: null,
  from_Time: new Date(),
  to_Time: new Date(),
  entry_Date: new Date(),
  booking_Date: new Date(),
  is_Cancelled: false,
  cancellation_Reason: "",
  notes: "",
  source_Id: null,
  sourceOther: "",
  modified_By: null,
  modified_On: null,
  selectedServices: [],
  selectedPackages: [],
  selectedPackageSeries: [],
};

const AddBookingPage = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [form, setForm] = useState(defaultForm);
  const [customers, setCustomers] = useState([]);
  const [customerSearchOptions, setCustomerSearchOptions] = useState([]);
  const [customerSearchLoading, setCustomerSearchLoading] = useState(false);
  const [searchTimeout, setSearchTimeout] = useState(null);
  const [selectedCustomerOption, setSelectedCustomerOption] = useState(null);
  const [employees, setEmployees] = useState([]);
  const [rooms, setRooms] = useState([]);
  const [equipments, setEquipments] = useState([]);
  const [services, setServices] = useState([]);
  const [packages, setPackages] = useState([]);
  const [packageSeries, setPackageSeries] = useState([]);
  const [serviceResources, setServiceResources] = useState([]);
  const [loading, setLoading] = useState(false);
  
  // Membership-related state
  const [customerMembership, setCustomerMembership] = useState(null);
  const [allCustomerMemberships, setAllCustomerMemberships] = useState([]);
  const [hasMembership, setHasMembership] = useState(false);
  const [membershipServices, setMembershipServices] = useState([]);
  const [membershipPackages, setMembershipPackages] = useState([]);
  const [membershipPackageSeries, setMembershipPackageSeries] = useState([]);
  const [formKey, setFormKey] = useState(0);
  
  // Availability checking
  const [timeAvailabilityResults, setTimeAvailabilityResults] = useState({});
  const [checkingTimeAvailability, setCheckingTimeAvailability] = useState(false);
  const [availabilityResults, setAvailabilityResults] = useState({});
  const [checkingAvailability, setCheckingAvailability] = useState(false);
  const [availabilityRefreshKey, setAvailabilityRefreshKey] = useState(0);
  
  // Consent form state
  const [showConsentModal, setShowConsentModal] = useState(false);
  const [consentData, setConsentData] = useState({
    witnessName: '',
    witnessSignature: '',
    guestSignature: ''
  });
  const [consentLoading, setConsentLoading] = useState(false);
  const [loggedInEmployeeName, setLoggedInEmployeeName] = useState('');
  const [sources, setSources] = useState([]);
  const [sourcesLoading, setSourcesLoading] = useState(false);

  // Quick Add Customer state
  const [showQuickAddCustomer, setShowQuickAddCustomer] = useState(false);
  const [showCustomerProfile, setShowCustomerProfile] = useState(false);

  // Membership modal state
  const [showMembershipModal, setShowMembershipModal] = useState(false);
  const [selectedMembershipServices, setSelectedMembershipServices] = useState([]);
  const [selectedMembershipPackages, setSelectedMembershipPackages] = useState([]);
  const [selectedMembershipPackageSeries, setSelectedMembershipPackageSeries] = useState([]);
  
  // Table-based selection for membership services
  const [tableSelection, setTableSelection] = useState({}); // { selectionKey: { selected: boolean, requiredQty: number, membershipIds: [] } }


  // Check permissions
  const permissions = JSON.parse(localStorage.getItem('permissions') || '[]');
  const hasPermission = hasPagePermissionWithAdmin(permissions, '/GmsAddBooking');
  const isViewOnly = hasViewOnlyPermission(permissions, '/GmsAddBooking');
  const hasEditOrHigher = hasEditOrHigherPermission(permissions, '/GmsAddBooking');
  const isAdmin = isUserAdmin();

  // Get user session
  const session = useMemo(() => getUserSession(), []);

  // Source dropdown options
  const sourceOptions = sources.map(source => ({
    value: source.Source_Id || source.source_Id,
    label: source.SourceName || source.sourceName || 'N/A'
  }));

  useEffect(() => {
    if (hasPermission) {
      loadInitialData();
      loadLoggedInEmployeeName();
      
      // Check if there's a date parameter in the URL (from calendar page)
      const urlParams = new URLSearchParams(location.search);
      const dateParam = urlParams.get('date');
      
      if (dateParam) {
        try {
          const selectedDate = new Date(dateParam);
          if (!isNaN(selectedDate.getTime())) {
            setForm(prev => ({
              ...prev,
              booking_Date: selectedDate
            }));
          }
        } catch (error) {
          console.error('Error parsing date from URL:', error);
        }
      }
    } else {
      window.location.href = '/Dashboard';
    }
  }, [hasPermission, location.search]);

  // Check availability when service resources change
  useEffect(() => {
    if (serviceResources.length > 0 && form.booking_Date) {
      serviceResources.forEach(resource => {
        if (resource.fromTime && resource.toTime) {
          checkTimeAvailability(resource.id, resource.fromTime, resource.toTime);
        }
      });
    }
  }, [serviceResources, form.booking_Date]);

  const loadInitialData = async () => {
    try {
      await Promise.all([
        loadCustomers(),
        loadEmployees(),
        loadRooms(),
        loadEquipments(),
        loadServices(),
        loadPackages(),
        loadPackageSeries(),
        loadSources(),
      ]);
    } catch (error) {
      console.error("Error loading initial data:", error);
      Swal.fire("Error", "Failed to load initial data", "error");
    }
  };

  const loadSources = async () => {
    setSourcesLoading(true);
    try {
      const filter = {
        Page: 1,
        PageSize: 1000,
      };
      const response = await getSources(filter);
      const sourcesList = response?.Sources || response?.sources || [];
      
      if (Array.isArray(sourcesList) && sourcesList.length > 0) {
        setSources(sourcesList);
      } else if (Array.isArray(response)) {
        setSources(response);
      } else {
        setSources([]);
      }
    } catch (error) {
      console.error("Error loading sources:", error);
      setSources([]);
    } finally {
      setSourcesLoading(false);
    }
  };

  const loadCustomers = async () => {
    try {
      const data = await getCustomers({});
      const formattedCustomers = data.map(customer => ({
        value: customer.customer_Id,
        label: customer.customer_NameEn || customer.customer_Name,
      }));
      setCustomers(formattedCustomers);
    } catch (error) {
      console.error('Error loading customers:', error);
      Swal.fire("Error", "Failed to load customers", "error");
    }
  };

  const searchCustomersManually = async (inputValue) => {
    if (!inputValue || inputValue.length < 3) {
      setCustomerSearchOptions([]);
      return;
    }
    
    try {
      setCustomerSearchLoading(true);
      const response = await searchCustomers(inputValue, 1, 20);
      const formattedOptions = response.items.map(customer => ({
        value: customer.customer_Id,
        label: `${customer.customer_NameEn || 'N/A'} — ${customer.customer_ContactNo || 'N/A'}`,
        customer: customer
      }));
      setCustomerSearchOptions(formattedOptions);
    } catch (error) {
      console.error('Error searching customers:', error);
      setCustomerSearchOptions([]);
    } finally {
      setCustomerSearchLoading(false);
    }
  };

  const handleCustomerAdded = async (newCustomer) => {
    const customerId = newCustomer.customer_Id || newCustomer.id;
    
    try {
      const customerData = await getCustomers({ customer_Id: customerId });
      if (customerData && customerData.length > 0) {
        const addedCustomer = customerData[0];
        setForm(prev => ({
          ...prev,
          customer_Id: customerId
        }));
        setSelectedCustomerOption({
          value: customerId,
          label: `${addedCustomer.customer_NameEn || 'N/A'} — ${addedCustomer.customer_ContactNo || 'N/A'}`
        });
        loadCustomers();
      } else {
        setForm(prev => ({
          ...prev,
          customer_Id: customerId
        }));
        setSelectedCustomerOption({
          value: customerId,
          label: `Customer ID: ${customerId}`
        });
        loadCustomers();
      }
    } catch (error) {
      console.error("Error fetching customer:", error);
      setForm(prev => ({
        ...prev,
        customer_Id: customerId
      }));
      setSelectedCustomerOption({
        value: customerId,
        label: `Customer ID: ${customerId}`
      });
    }
  };

  const loadEmployees = async () => {
    try {
      const data = await getEmployees({});
      const formattedEmployees = data.map(emp => ({
        value: emp.employee_Id,
        label: emp.employee_NameEn || emp.employee_Name,
      }));
      setEmployees(formattedEmployees);
    } catch (error) {
      console.error('Error loading employees:', error);
      Swal.fire("Error", "Failed to load employees", "error");
    }
  };

  const loadRooms = async () => {
    try {
      const data = await getRooms({});
      const formattedRooms = data.map(room => ({
        value: room.room_Id,
        label: room.room_Name_English || room.room_Name_Arabic || room.room_Name,
      }));
      setRooms(formattedRooms);
    } catch (error) {
      console.error('Error loading rooms:', error);
      Swal.fire("Error", "Failed to load rooms", "error");
    }
  };

  const loadServices = async () => {
    try {
      const data = await getSpaServices({ status: true });
      const formattedServices = data.map(service => ({
        value: service.spaService_Id,
        label: service.spaServiceName_En || service.spaServiceName_Ar || service.spaService_NameEn || service.spaService_Name,
        duration: service.duration || 30,
      }));
      setServices(formattedServices);
    } catch (error) {
      console.error('Error loading services:', error);
      Swal.fire("Error", "Failed to load services", "error");
    }
  };

  const loadPackages = async () => {
    try {
      const data = await getSpaPackages({ status: true });
      const formattedPackages = data.map(pkg => ({
        value: pkg.packageId,
        label: pkg.packageName_En || pkg.packageName_Ar || pkg.package_NameEn || pkg.package_Name,
      }));
      setPackages(formattedPackages);
    } catch (error) {
      console.error('Error loading packages:', error);
      Swal.fire("Error", "Failed to load packages", "error");
    }
  };

  const loadPackageSeries = async () => {
    try {
      const data = await getSpaPackageSeries({ status: true });
      const formattedPackageSeries = data.map(series => ({
        value: series.seriesId,
        label: series.seriesName_En || series.seriesName_Ar,
      }));
      setPackageSeries(formattedPackageSeries);
    } catch (error) {
      console.error('Error loading package series:', error);
      Swal.fire("Error", "Failed to load package series", "error");
    }
  };

  const loadLoggedInEmployeeName = async () => {
    try {
      const authUser = JSON.parse(localStorage.getItem('authUser') || '{}');
      const user = JSON.parse(localStorage.getItem('user') || '{}');
      const employeeId = authUser.employeeId || user.user_EmployeeId;
      
      if (employeeId) {
        const employees = await getEmployees({ employee_Id: employeeId });
        if (employees && employees.length > 0) {
          const employee = employees[0];
          const employeeName = employee.employee_NameEn || employee.employee_Name || '';
          setLoggedInEmployeeName(employeeName);
          setConsentData(prev => ({
            ...prev,
            witnessName: prev.witnessName || employeeName
          }));
        }
      }
    } catch (error) {
      console.error('Error loading logged-in employee name:', error);
    }
  };

  const loadEquipments = async () => {
    try {
      const data = await getEquipments({ status: true });
      const formattedEquipments = data.map(equipment => ({
        value: equipment.eq_Id,
        label: equipment.eq_Name_English || equipment.eq_Name_Arabic,
      }));
      setEquipments(formattedEquipments);
    } catch (error) {
      console.error('Error loading equipments:', error);
      Swal.fire("Error", "Failed to load equipments", "error");
    }
  };

  const fetchPackageServices = async (packageIds) => {
    try {
      const allPackageServices = [];
      
      for (const packageId of packageIds) {
        const packageData = await getSpaPackageById(packageId);
        if (packageData && packageData.details) {
          packageData.details.forEach(detail => {
            const service = services.find(s => s.value === detail.service_Id);
            if (service) {
              allPackageServices.push({
                ...service,
                packageId: packageId,
                packageName: packageData.packageName_En || packageData.packageName_Ar,
                serviceId: detail.service_Id,
                quantity: detail.quantity || 1,
                duration: service.duration || 30
              });
            }
          });
        }
      }
      
      return allPackageServices;
    } catch (error) {
      console.error('Error fetching package services:', error);
      return [];
    }
  };

  const checkCustomerMembership = async (customerId) => {
    if (!customerId) {
      setCustomerMembership(null);
      setAllCustomerMemberships([]);
      setHasMembership(false);
      setMembershipServices([]);
      setMembershipPackages([]);
      setMembershipPackageSeries([]);
      return;
    }

    try {
      const memberships = await getCustomerMemberships({ customerId: customerId });
      
      if (memberships && memberships.length > 0) {
        setAllCustomerMemberships(memberships);
        const membership = memberships[0];
        setCustomerMembership(membership);
        setHasMembership(true);
        
        let allPendingDetails = [];
        let allSeriesIds = new Set();
        
        memberships.forEach(m => {
          if (m.details && m.details.length > 0) {
            const pendingDetails = m.details.filter(detail => detail.completionStatus === "Pending");
            allPendingDetails = [...allPendingDetails, ...pendingDetails];
          }
          if (m.installments && m.installments.length > 0) {
            m.installments.forEach(inst => {
              if (inst.seriesId) {
                allSeriesIds.add(inst.seriesId);
              }
            });
          }
        });
        
        const uniqueServiceIds = [...new Set(allPendingDetails.map(detail => detail.serviceId).filter(Boolean))];
        const uniquePackageIds = [...new Set(allPendingDetails.map(detail => detail.packageId).filter(Boolean))];
        const uniqueSeriesIds = Array.from(allSeriesIds);
        
        const membershipServicesData = [];
        const membershipPackagesData = [];
        const membershipPackageSeriesData = [];
        
        uniqueServiceIds.forEach(serviceId => {
          const service = services.find(s => s.value === serviceId);
          if (service) {
            membershipServicesData.push(service);
          }
        });
        
        uniquePackageIds.forEach(packageId => {
          const pkg = packages.find(p => p.value === packageId);
          if (pkg) {
            membershipPackagesData.push(pkg);
          }
        });
        
        uniqueSeriesIds.forEach(seriesId => {
          const series = packageSeries.find(s => s.value === seriesId);
          if (series) {
            membershipPackageSeriesData.push(series);
          }
        });
        
        setMembershipServices(membershipServicesData);
        setMembershipPackages(membershipPackagesData);
        setMembershipPackageSeries(membershipPackageSeriesData);
      } else {
        setCustomerMembership(null);
        setAllCustomerMemberships([]);
        setHasMembership(false);
        setMembershipServices([]);
        setMembershipPackages([]);
        setMembershipPackageSeries([]);
      }
    } catch (error) {
      console.error('Error checking customer membership:', error);
      setCustomerMembership(null);
      setAllCustomerMemberships([]);
      setHasMembership(false);
      setMembershipServices([]);
      setMembershipPackages([]);
      setMembershipPackageSeries([]);
    }
  };

  const checkTimeAvailability = async (resourceId, fromTime, toTime) => {
    if (!form.booking_Date || !fromTime || !toTime) {
      return;
    }

    try {
      setCheckingTimeAvailability(true);
      
      const bookingDate = new Date(form.booking_Date);
      const bookingDateStr = formatLocalDate(bookingDate);
      const fromTimeStr = formatLocalDateTime(fromTime);
      const toTimeStr = formatLocalDateTime(toTime);

      const timeData = {
        booking_Id: null,
        booking_Date: bookingDateStr,
        from_Time: fromTimeStr,
        to_Time: toTimeStr
      };

      const results = await checkAvailabilityForTime(timeData);
      
      setTimeAvailabilityResults(prev => ({
        ...prev,
        [resourceId]: results
      }));
      
      // Increment refresh key to force Select components to refresh
      setAvailabilityRefreshKey(prev => prev + 1);
    } catch (error) {
      console.error('Error checking time availability:', error);
    } finally {
      setCheckingTimeAvailability(false);
    }
  };

  // Check availability for service resources
  const checkAvailabilityForSelection = async () => {
    if (!form.booking_Date || serviceResources.length === 0) {
      return;
    }

    try {
      setCheckingAvailability(true);
      
      const bookingDate = new Date(form.booking_Date);
      const bookingDateStr = formatLocalDate(bookingDate);

      // Create service resources for availability check
      const serviceResourcesForCheck = serviceResources
        .filter(resource => resource.employeeId || resource.roomId) // Only include resources with employee or room
        .map(resource => ({
          employee_Id: resource.employeeId || null,
          room_Id: resource.roomId || null,
          from_Time: resource.fromTime ? formatLocalDateTime(resource.fromTime) : null,
          to_Time: resource.toTime ? formatLocalDateTime(resource.toTime) : null
        }));

      // Don't make API call if no valid resources
      if (serviceResourcesForCheck.length === 0) {
        setAvailabilityResults({});
        return;
      }

      const availabilityData = {
        booking_Id: null, // Always null for new bookings
        booking_Date: bookingDateStr,
        serviceResources: serviceResourcesForCheck
      };

      console.log('Availability check data:', availabilityData);
      const results = await checkAvailability(availabilityData);
      console.log('Availability results:', results);
      
      // Convert results to a map for easy lookup
      const availabilityMap = {};
      results.forEach(result => {
        availabilityMap[`${result.type}_${result.id}`] = result;
      });
      
      setAvailabilityResults(availabilityMap);
    } catch (error) {
      console.error('Error checking availability:', error);
      // Don't show error to user as this is a background check
    } finally {
      setCheckingAvailability(false);
    }
  };

  const formatOptionsWithAvailability = (options, type, resourceId) => {
    const timeAvailability = timeAvailabilityResults[resourceId];
    
    return options.map(option => {
      let isAvailable = true;
      
      if (timeAvailability) {
        if (type === 'Employee') {
          const employee = timeAvailability.availableEmployees?.find(emp => emp.employee_Id === option.value);
          isAvailable = employee ? employee.isAvailable : true;
        } else if (type === 'Room') {
          const room = timeAvailability.availableRooms?.find(room => room.room_Id === option.value);
          isAvailable = room ? room.isAvailable : true;
        }
      }
      
      return {
        ...option,
        isDisabled: !isAvailable,
        label: (
          <div className="d-flex align-items-center">
            <span className={`me-2 ${isAvailable ? 'text-success' : 'text-danger'}`}>
              {isAvailable ? '✓' : '✗'}
            </span>
            <span>{option.label}</span>
          </div>
        )
      };
    });
  };

  const handleFormChange = async (field, value) => {
    setForm(prev => ({
      ...prev,
      [field]: value
    }));

    if (field === 'customer_Id') {
      if (value) {
        checkCustomerMembership(value);
      } else {
        setServiceResources([]);
        setForm(prev => ({
          ...prev,
          selectedServices: [],
          selectedPackages: [],
          selectedPackageSeries: []
        }));
        setCustomerMembership(null);
        setAllCustomerMemberships([]);
        setHasMembership(false);
        setMembershipServices([]);
        setMembershipPackages([]);
        setMembershipPackageSeries([]);
        setConsentData({
          witnessName: loggedInEmployeeName,
          witnessSignature: '',
          guestSignature: ''
        });
      }
    }

    if (field === 'selectedPackages') {
      // Check if trying to add packages when package service exists
      const bookingTypes = checkBookingServiceTypes();
      if (bookingTypes.hasPackageService) {
        Swal.fire({
          icon: "warning",
          title: "Cannot Add Packages",
          html: "This booking already contains services from a package membership.<br/><br/>Package services must be booked separately. Please create a new booking for packages.",
          confirmButtonText: "OK"
        });
        setForm(prev => ({ ...prev, selectedPackages: [] }));
        return;
      }

      if (value && value.length > 0) {
        const packageIds = value.map(pkg => pkg.value);
        const packageServices = await fetchPackageServices(packageIds);
        
        setServiceResources(prev => {
          const filtered = prev.filter(resource => !(resource.packageId && !resource.membershipId));
          const newResources = packageServices.map((service, index) => {
            const baseTime = new Date();
            baseTime.setHours(9, 0, 0, 0);
            
            let cumulativeMinutes = 0;
            for (let i = 0; i < index; i++) {
              const prevService = packageServices[i];
              const prevDuration = prevService.duration || 30;
              cumulativeMinutes += prevDuration;
            }
            
            const fromTime = new Date(baseTime.getTime() + (cumulativeMinutes * 60000));
            const duration = service.duration || 30;
            const toTime = new Date(fromTime.getTime() + (duration * 60000));
            
            return {
              id: `pkg-${service.packageId}-${service.serviceId}-${Date.now()}-${Math.random()}-${index}`,
              serviceId: service.serviceId,
              serviceName: service.label,
              packageId: service.packageId,
              packageName: service.packageName,
              employeeId: null,
              roomId: null,
              equipmentId: null,
              fromTime: fromTime,
              toTime: toTime
            };
          });
          const updatedResources = [...filtered, ...newResources];
          
          // Trigger availability checks after resources are added
          setTimeout(() => {
            updatedResources.forEach(resource => {
              if (resource.fromTime && resource.toTime) {
                checkTimeAvailability(resource.id, resource.fromTime, resource.toTime);
              }
            });
            checkAvailabilityForSelection();
          }, 500);
          
          return updatedResources;
        });
      } else {
        setServiceResources(prev => prev.filter(resource => !(resource.packageId && !resource.membershipId)));
      }
    }

    if (field === 'selectedServices') {
      // Check if trying to add regular services when package service exists
      const bookingTypes = checkBookingServiceTypes();
      if (bookingTypes.hasPackageService) {
        Swal.fire({
          icon: "warning",
          title: "Cannot Add Regular Services",
          html: "This booking already contains services from a package membership.<br/><br/>Package services must be booked separately. Please create a new booking for regular services.",
          confirmButtonText: "OK"
        });
        setForm(prev => ({ ...prev, selectedServices: [] }));
        return;
      }

      if (value && value.length > 0) {
        setServiceResources(prev => {
          const existingIndividualResources = prev.filter(resource => !resource.packageId && !resource.membershipId);
          const selectedServiceIds = value.map(service => service.value);
          const preservedResources = existingIndividualResources.filter(resource => 
            selectedServiceIds.includes(resource.serviceId)
          );
          const existingServiceIds = preservedResources.map(resource => resource.serviceId);
          const newServices = value.filter(service => !existingServiceIds.includes(service.value));
          
          const newResources = newServices.map((service, index) => {
            const fullIndex = value.findIndex(s => s.value === service.value);
            const baseTime = new Date();
            baseTime.setHours(9, 0, 0, 0);
            
            let cumulativeMinutes = 0;
            for (let i = 0; i < fullIndex; i++) {
              const prevService = value[i];
              const prevDuration = prevService.duration || 30;
              cumulativeMinutes += prevDuration;
            }
            
            const fromTime = new Date(baseTime.getTime() + (cumulativeMinutes * 60000));
            const duration = service.duration || 30;
            const toTime = new Date(fromTime.getTime() + (duration * 60000));
            
            return {
              id: `srv-${service.value}-${Date.now()}-${Math.random()}-${fullIndex}`,
              serviceId: service.value,
              serviceName: service.label,
              packageId: null,
              packageName: null,
              employeeId: null,
              roomId: null,
              equipmentId: null,
              fromTime: fromTime,
              toTime: toTime
            };
          });
          
          const otherResources = prev.filter(resource => resource.packageId || resource.membershipId);
          const updatedResources = [...otherResources, ...preservedResources, ...newResources];
          
          // Trigger availability checks after resources are added
          setTimeout(() => {
            updatedResources.forEach(resource => {
              if (resource.fromTime && resource.toTime) {
                checkTimeAvailability(resource.id, resource.fromTime, resource.toTime);
              }
            });
            checkAvailabilityForSelection();
          }, 500);
          
          return updatedResources;
        });
      } else {
        setServiceResources(prev => prev.filter(resource => !(!resource.packageId && !resource.membershipId)));
      }
    }

    if (field === 'booking_Date') {
      // Use setTimeout to avoid too many API calls while user is typing
      setTimeout(() => {
        // Trigger time-based availability checks for each resource
        serviceResources.forEach(resource => {
          if (resource.fromTime && resource.toTime) {
            checkTimeAvailability(resource.id, resource.fromTime, resource.toTime);
          }
        });
        // Also trigger bulk availability check
        checkAvailabilityForSelection();
      }, 500);
    }
  };

  const handleResourceChange = (resourceId, field, value) => {
    let updatedResource = null;
    
    setServiceResources(prev => {
      const updated = prev.map(resource => {
        if (resource.id === resourceId) {
          updatedResource = { ...resource, [field]: value || null };
          return updatedResource;
        }
        return resource;
      });
      return updated;
    });
    
    // Check time-based availability when time changes
    if (['fromTime', 'toTime'].includes(field) && updatedResource) {
      // Use the updated resource directly instead of reading from state
      const fromTime = field === 'fromTime' ? value : updatedResource.fromTime;
      const toTime = field === 'toTime' ? value : updatedResource.toTime;
      
      if (fromTime && toTime) {
        setCheckingTimeAvailability(true);
        setTimeout(() => {
          checkTimeAvailability(resourceId, fromTime, toTime).finally(() => {
            setCheckingTimeAvailability(false);
            // Also trigger bulk availability check to refresh all dropdowns
            checkAvailabilityForSelection();
          });
        }, 500);
      }
    }
    
    // Check availability when employee or room changes
    if (['employeeId', 'roomId'].includes(field)) {
      setTimeout(() => {
        checkAvailabilityForSelection();
      }, 500);
    }
  };

  const handleRemoveService = (resourceId) => {
    setServiceResources(prev => prev.filter(resource => resource.id !== resourceId));
  };

  // Build membership table data
  const buildMembershipTableData = () => {
    if (!allCustomerMemberships || allCustomerMemberships.length === 0) {
      return [];
    }

    // Count how many instances of each service are already added to serviceResources
    const addedServiceCounts = {};
    serviceResources
      .filter(r => r.membershipId) // Only count membership services
      .forEach(r => {
        const serviceId = r.serviceId;
        addedServiceCounts[serviceId] = (addedServiceCounts[serviceId] || 0) + 1;
      });

    // Map to store aggregated service data
    const serviceMap = new Map();

    // Process all memberships
    allCustomerMemberships.forEach(membership => {
      if (!membership || !membership.details || membership.details.length === 0) return;

      // Filter only pending details and exclude products
      const pendingDetails = membership.details.filter(detail => 
        detail.completionStatus === "Pending" && 
        detail.serviceId && 
        !detail.productId // Exclude products
      );
      
      if (pendingDetails.length === 0) return;

      // Process each pending service detail
      pendingDetails.forEach(detail => {
        const serviceId = detail.serviceId;
        const packageId = detail.packageId || null;

        // Get service name
        const service = services.find(s => s.value === serviceId);
        if (!service) return;

        // Get package name if available
        let packageName = null;
        if (packageId) {
          const pkg = packages.find(p => p.value === packageId);
          packageName = pkg ? pkg.label : null;
        }

        // Create composite key: serviceId_packageId (use 'direct' for null packageId)
        const compositeKey = packageId ? `${serviceId}_${packageId}` : `${serviceId}_direct`;

        // Aggregate service data by composite key
        if (!serviceMap.has(compositeKey)) {
          serviceMap.set(compositeKey, {
            serviceId: serviceId,
            serviceName: service.label,
            packageId: packageId,
            packageName: packageName || null,
            availableQty: 0,
            membershipIds: new Set()
          });
        }

        const serviceData = serviceMap.get(compositeKey);
        serviceData.availableQty += 1; // Count each pending instance
        if (membership.membershipId) {
          serviceData.membershipIds.add(membership.membershipId);
        }
      });
    });

    // Convert Map to array, format for table, and sort
    return Array.from(serviceMap.values())
      .map(serviceData => {
        const serviceId = serviceData.serviceId;
        const packageId = serviceData.packageId;
        const alreadyAdded = addedServiceCounts[serviceId] || 0;
        const availableQty = Math.max(0, serviceData.availableQty - alreadyAdded);
        
        // Only include services that still have available quantity
        if (availableQty <= 0) return null;
        
        // Create a unique key for this service+package combination
        const selectionKey = packageId ? `${serviceId}_${packageId}` : `${serviceId}_direct`;
        
        return {
          serviceId: serviceId,
          serviceName: serviceData.serviceName,
          availableQty: availableQty,
          packageNames: serviceData.packageName || 'Direct Service',
          packageId: packageId,
          membershipIds: Array.from(serviceData.membershipIds),
          selectionKey: selectionKey
        };
      })
      .filter(item => item !== null) // Remove services with no available quantity
      .sort((a, b) => {
        // Sort by packageId first, then by service name
        const aHasPackage = a.packageId !== null;
        const bHasPackage = b.packageId !== null;
        
        if (aHasPackage && !bHasPackage) return -1;
        if (!aHasPackage && bHasPackage) return 1;
        
        if (aHasPackage && bHasPackage) {
          if (a.packageId !== b.packageId) {
            return a.packageId - b.packageId;
          }
          return a.serviceName.localeCompare(b.serviceName);
        }
        
        return a.serviceName.localeCompare(b.serviceName);
      });
  };

  const openMembershipModal = () => {
    if (hasMembership && allCustomerMemberships.length > 0) {
      setShowMembershipModal(true);
      // Reset selections when opening modal
      setSelectedMembershipServices([]);
      setSelectedMembershipPackages([]);
      setSelectedMembershipPackageSeries([]);
      // Reset table selection
      setTableSelection({});
    }
  };

  const closeMembershipModal = () => {
    setShowMembershipModal(false);
    setSelectedMembershipServices([]);
    setSelectedMembershipPackages([]);
    setSelectedMembershipPackageSeries([]);
    // Reset table selection
    setTableSelection({});
  };

  // Handle table-based service selection
  const handleTableServiceSelection = (selectionKey, checked) => {
    setTableSelection(prev => {
      const newSelection = { ...prev };
      if (checked) {
        // Get service data to set default required quantity
        const tableData = buildMembershipTableData();
        const serviceData = tableData.find(s => s.selectionKey === selectionKey);
        if (serviceData) {
          newSelection[selectionKey] = {
            selected: true,
            requiredQty: 1, // Default to 1
            membershipIds: serviceData.membershipIds
          };
        }
      } else {
        // Remove selection
        delete newSelection[selectionKey];
      }
      return newSelection;
    });
  };

  // Handle required quantity change
  const handleRequiredQtyChange = (selectionKey, qty) => {
    setTableSelection(prev => {
      const newSelection = { ...prev };
      if (newSelection[selectionKey]) {
        const tableData = buildMembershipTableData();
        const serviceData = tableData.find(s => s.selectionKey === selectionKey);
        const availableQty = serviceData ? serviceData.availableQty : 0;
        
        // Allow empty string for deletion, otherwise parse the value
        if (qty === '' || qty === null || qty === undefined) {
          newSelection[selectionKey] = {
            ...newSelection[selectionKey],
            requiredQty: ''
          };
        } else {
          const requestedQty = parseInt(qty);
          if (!isNaN(requestedQty) && requestedQty > 0) {
            newSelection[selectionKey] = {
              ...newSelection[selectionKey],
              requiredQty: Math.min(requestedQty, availableQty)
            };
          } else {
            newSelection[selectionKey] = {
              ...newSelection[selectionKey],
              requiredQty: ''
            };
          }
        }
      }
      return newSelection;
    });
  };

  // Helper function to check what types of services are already in the booking
  const checkBookingServiceTypes = () => {
    const hasPackageService = serviceResources.some(resource => {
      if (!resource.membershipId) return false;
      // Find the membership to check entity type
      const membership = allCustomerMemberships.find(m => m.membershipId === resource.membershipId);
      return membership && (membership.entity_Type === 'PAC' || membership.entity_Type === 'pac');
    });

    const hasPackageSeriesService = serviceResources.some(resource => {
      if (!resource.membershipId) return false;
      const membership = allCustomerMemberships.find(m => m.membershipId === resource.membershipId);
      return membership && (membership.entity_Type === 'SEP' || membership.entity_Type === 'sep');
    });

    const hasNonMembershipService = serviceResources.some(resource => !resource.membershipId);

    // Get package series IDs from existing services
    const existingSeriesIds = new Set();
    serviceResources.forEach(resource => {
      if (resource.membershipId) {
        const membership = allCustomerMemberships.find(m => m.membershipId === resource.membershipId);
        if (membership && (membership.entity_Type === 'SEP' || membership.entity_Type === 'sep')) {
          existingSeriesIds.add(membership.entity_Id);
        }
      }
    });

    return {
      hasPackageService,
      hasPackageSeriesService,
      hasNonMembershipService,
      existingSeriesIds: Array.from(existingSeriesIds)
    };
  };

  // Apply membership selections from table
  const applyMembershipSelections = async () => {
    const bookingTypes = checkBookingServiceTypes();
    
    // Check if trying to add package service when other types exist
    const selectedServices = [];
    const tableData = buildMembershipTableData();
    let hasPackageServiceInSelection = false;
    let hasPackageSeriesServiceInSelection = false;
    const selectedSeriesIds = new Set();
    
    // First pass: Check what types are being selected
    Object.keys(tableSelection).forEach(selectionKey => {
      const selection = tableSelection[selectionKey];
      if (selection && selection.selected) {
        const serviceData = tableData.find(s => s.selectionKey === selectionKey);
        if (serviceData) {
          // Find membership to check entity type
          selection.membershipIds.forEach(membershipId => {
            const membership = allCustomerMemberships.find(m => m.membershipId === membershipId);
            if (membership) {
              if (membership.entity_Type === 'PAC' || membership.entity_Type === 'pac') {
                hasPackageServiceInSelection = true;
              } else if (membership.entity_Type === 'SEP' || membership.entity_Type === 'sep') {
                hasPackageSeriesServiceInSelection = true;
                selectedSeriesIds.add(membership.entity_Id);
              }
            }
          });
        }
      }
    });

    // Validation: Block mixing incompatible types
    if (hasPackageServiceInSelection) {
      if (bookingTypes.hasNonMembershipService || bookingTypes.hasPackageSeriesService) {
        Swal.fire({
          icon: "warning",
          title: "Cannot Mix Service Types",
          html: "Services from package memberships cannot be mixed with other services or package series services.<br/><br/>Please create a separate booking for package services.",
          confirmButtonText: "OK"
        });
        return;
      }
    }

    if (hasPackageSeriesServiceInSelection) {
      if (bookingTypes.hasPackageService) {
        Swal.fire({
          icon: "warning",
          title: "Cannot Mix Service Types",
          html: "Services from package memberships cannot be mixed with package series services.<br/><br/>Please create a separate booking for package services.",
          confirmButtonText: "OK"
        });
        return;
      }
      
      // Check if trying to add services from different series
      if (bookingTypes.existingSeriesIds.length > 0) {
        const isDifferentSeries = Array.from(selectedSeriesIds).some(seriesId => 
          !bookingTypes.existingSeriesIds.includes(seriesId)
        );
        if (isDifferentSeries) {
          Swal.fire({
            icon: "warning",
            title: "Cannot Mix Package Series",
            html: "Services from different package series cannot be mixed in the same booking.<br/><br/>Please select services from the same package series only.",
            confirmButtonText: "OK"
          });
          return;
        }
      }
    }

    if (bookingTypes.hasPackageService) {
      if (hasPackageSeriesServiceInSelection || bookingTypes.hasNonMembershipService) {
        Swal.fire({
          icon: "warning",
          title: "Cannot Add Other Services",
          html: "This booking already contains services from a package membership.<br/><br/>Package services must be booked separately. Please create a new booking for other services.",
          confirmButtonText: "OK"
        });
        return;
      }
    }

    // Process table selections
    Object.keys(tableSelection).forEach(selectionKey => {
      const selection = tableSelection[selectionKey];
      if (selection && selection.selected) {
        const serviceData = tableData.find(s => s.selectionKey === selectionKey);
        if (serviceData) {
          const service = services.find(s => s.value === serviceData.serviceId);
          if (service) {
            // Get required quantity (default to 1 if missing)
            let requiredQty = selection.requiredQty;
            if (!requiredQty || requiredQty === '' || requiredQty === 0) {
              requiredQty = 1;
            }
            requiredQty = Math.min(requiredQty, serviceData.availableQty);
            
            // Find corresponding membership details
            const membershipDetails = [];
            allCustomerMemberships.forEach(membership => {
              if (!membership.details) return;
              const pendingDetails = membership.details.filter((detail) => (
                detail.serviceId === serviceData.serviceId &&
                detail.completionStatus === "Pending" &&
                !detail.productId &&
                selection.membershipIds.includes(membership.membershipId) &&
                (serviceData.packageId === null ? detail.packageId === null : detail.packageId === serviceData.packageId)
              ));
              membershipDetails.push(...pendingDetails);
            });
            
            // Add services up to required quantity
            for (let i = 0; i < requiredQty && i < membershipDetails.length; i++) {
              const detail = membershipDetails[i];
              selectedServices.push({
                ...service,
                membershipId: detail.membershipId,
                packageId: detail.packageId,
                detailId: detail.detailId || null
              });
            }
          }
        }
      }
    });

    // Update service resources for membership selections
    const newServiceResources = [...serviceResources];
    
    // Calculate time slots for membership services
    const baseTime = new Date();
    baseTime.setHours(9, 0, 0, 0);
    let cumulativeMinutes = 0;
    
    // Add selected services to service resources
    selectedServices.forEach(service => {
      const resourceId = `mem-srv-${service.serviceId}-${service.membershipId}-${service.detailId || Date.now()}`;
      
      // Check if this exact service instance is already added
      if (!newServiceResources.find(r => 
        r.serviceId === service.value && 
        r.membershipId === service.membershipId &&
        r.detailId === service.detailId
      )) {
        const packageId = service.packageId || null;
        const packageName = packageId ? packages.find(p => p.value === packageId)?.label : null;
        
        // Get service duration
        const serviceFromList = services.find(s => s.value === service.value);
        const duration = serviceFromList?.duration || service.duration || 30;
        
        // Calculate time slots using service duration
        const fromTime = new Date(baseTime.getTime() + (cumulativeMinutes * 60000));
        const toTime = new Date(fromTime.getTime() + (duration * 60000));
        
        newServiceResources.push({
          id: resourceId,
          serviceId: service.value,
          serviceName: service.label,
          packageId: packageId,
          packageName: packageName,
          membershipId: service.membershipId,
          detailId: service.detailId,
          employeeId: null,
          roomId: null,
          equipmentId: null,
          fromTime: fromTime,
          toTime: toTime
        });
        
        // Update cumulative minutes for next service
        cumulativeMinutes += duration;
      }
    });

    setServiceResources(newServiceResources);
    closeMembershipModal();
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!form.customer_Id || !form.source_Id) {
      Swal.fire("Validation Error", "Please fill all required fields (Customer and Source)", "error");
      return;
    }

    if (serviceResources.length === 0) {
      Swal.fire("Validation Error", "Please select at least one service or package", "error");
      return;
    }

    for (const resource of serviceResources) {
      if (resource.fromTime && resource.toTime) {
        if (new Date(resource.toTime) <= new Date(resource.fromTime)) {
          Swal.fire("Validation Error", `To time must be after from time for service: ${resource.serviceName}`, "error");
          return;
        }
      }
    }

    try {
      setLoading(true);
      
      const authUser = JSON.parse(localStorage.getItem("authUser") || "{}");
      const currentUserId = authUser.userId || authUser.user_Id || 1;
      
      const details = [];
      const resourceDetails = [];
      let detailIndex = 1;

      const individualServices = serviceResources.filter(r => !r.packageId && !r.membershipId);
      const packageServices = serviceResources.filter(r => r.packageId && !r.membershipId);
      const membershipServices = serviceResources.filter(r => r.membershipId);
      
      const uniquePackages = [...new Set(packageServices.map(r => r.packageId))];
      const uniqueMemberships = [...new Set(membershipServices.map(r => r.membershipId))];
      
      if (individualServices.length > 0) {
        individualServices.forEach(service => {
          const detailId = detailIndex++;
          const detailObj = {
            tempDetailId: detailId,
            entity_Id: service.serviceId,
            entity_Type: 'ser',
            is_Cancelled: false,
            cancel_Reason: null,
            created_By: currentUserId,
            created_On: new Date().toISOString(),
            modified_By: currentUserId,
            modified_On: new Date().toISOString(),
          };
          details.push(detailObj);
          
          const bookingDate = new Date(form.booking_Date);
          const fromTime = service.fromTime ? new Date(bookingDate.getFullYear(), bookingDate.getMonth(), bookingDate.getDate(), 
                                   service.fromTime.getHours(), service.fromTime.getMinutes(), service.fromTime.getSeconds()) : null;
          const toTime = service.toTime ? new Date(bookingDate.getFullYear(), bookingDate.getMonth(), bookingDate.getDate(), 
                                 service.toTime.getHours(), service.toTime.getMinutes(), service.toTime.getSeconds()) : null;

          const resourceDetailObj = {
            tempDetailId: detailId,
            service_Id: service.serviceId,
            package_Id: null,
            membership_Id: null,
            room_Id: service.roomId,
            equipment_Id: service.equipmentId,
            employee_Id: service.employeeId,
            from_Time: fromTime ? formatLocalDateTime(fromTime) : null,
            to_Time: toTime ? formatLocalDateTime(toTime) : null,
            modified_By: currentUserId,
            modified_On: new Date().toISOString(),
            completionStatus: null,
            cancellation_Reason: null,
          };
          resourceDetails.push(resourceDetailObj);
        });
      }
      
      if (uniquePackages.length > 0) {
        uniquePackages.forEach(packageId => {
          const detailId = detailIndex++;
          const packageDetailObj = {
            tempDetailId: detailId,
            entity_Id: packageId,
            entity_Type: 'pac',
            is_Cancelled: false,
            cancel_Reason: null,
            created_By: currentUserId,
            created_On: new Date().toISOString(),
            modified_By: currentUserId,
            modified_On: new Date().toISOString(),
          };
          details.push(packageDetailObj);
          
          const servicesInPackage = packageServices.filter(r => r.packageId === packageId);
          servicesInPackage.forEach(service => {
            const bookingDate = new Date(form.booking_Date);
            const fromTime = service.fromTime ? new Date(bookingDate.getFullYear(), bookingDate.getMonth(), bookingDate.getDate(), 
                                     service.fromTime.getHours(), service.fromTime.getMinutes(), service.fromTime.getSeconds()) : null;
            const toTime = service.toTime ? new Date(bookingDate.getFullYear(), bookingDate.getMonth(), bookingDate.getDate(), 
                                   service.toTime.getHours(), service.toTime.getMinutes(), service.toTime.getSeconds()) : null;

            const packageResourceDetailObj = {
              tempDetailId: detailId,
              service_Id: service.serviceId,
              package_Id: packageId,
              membership_Id: null,
              room_Id: service.roomId,
              equipment_Id: service.equipmentId,
              employee_Id: service.employeeId,
              from_Time: formatLocalDateTime(fromTime),
              to_Time: formatLocalDateTime(toTime),
              modified_By: currentUserId,
              modified_On: new Date().toISOString(),
              completionStatus: null,
              cancellation_Reason: null,
            };
            resourceDetails.push(packageResourceDetailObj);
          });
        });
      }
      
      if (uniqueMemberships.length > 0) {
        uniqueMemberships.forEach(membershipId => {
          const detailId = detailIndex++;
          const membershipDetailObj = {
            tempDetailId: detailId,
            entity_Id: membershipId,
            entity_Type: 'mem',
            is_Cancelled: false,
            cancel_Reason: null,
            created_By: currentUserId,
            created_On: new Date().toISOString(),
            modified_By: currentUserId,
            modified_On: new Date().toISOString(),
          };
          details.push(membershipDetailObj);
          
          const servicesInMembership = membershipServices.filter(r => r.membershipId === membershipId);
          servicesInMembership.forEach(service => {
            const bookingDate = new Date(form.booking_Date);
            const fromTime = service.fromTime ? new Date(bookingDate.getFullYear(), bookingDate.getMonth(), bookingDate.getDate(), 
                                     service.fromTime.getHours(), service.fromTime.getMinutes(), service.fromTime.getSeconds()) : null;
            const toTime = service.toTime ? new Date(bookingDate.getFullYear(), bookingDate.getMonth(), bookingDate.getDate(), 
                                   service.toTime.getHours(), service.toTime.getMinutes(), service.toTime.getSeconds()) : null;

            const membershipResourceDetailObj = {
              tempDetailId: detailId,
              service_Id: service.serviceId,
              package_Id: service.packageId,
              membership_Id: membershipId,
              room_Id: service.roomId,
              equipment_Id: service.equipmentId,
              employee_Id: service.employeeId,
              from_Time: formatLocalDateTime(fromTime),
              to_Time: formatLocalDateTime(toTime),
              modified_By: currentUserId,
              modified_On: new Date().toISOString(),
              completionStatus: null,
              cancellation_Reason: null,
            };
            resourceDetails.push(membershipResourceDetailObj);
          });
        });
      }
      
      const bookingDate = new Date(form.booking_Date);
      const allFromTimes = resourceDetails.map(rd => rd.from_Time ? new Date(rd.from_Time) : null).filter(d => d !== null);
      const allToTimes = resourceDetails.map(rd => rd.to_Time ? new Date(rd.to_Time) : null).filter(d => d !== null);
      
      const earliestFromTime = allFromTimes.length > 0 ? new Date(Math.min(...allFromTimes)) : bookingDate;
      const latestToTime = allToTimes.length > 0 ? new Date(Math.max(...allToTimes)) : bookingDate;

      const bookingData = {
        ...form,
        booking_Date: formatLocalDate(new Date(form.booking_Date)),
        from_Time: formatLocalDateTime(earliestFromTime),
        to_Time: formatLocalDateTime(latestToTime),
        entry_Date: formatLocalDateTime(new Date()),
        source_Id: form.source_Id || null,
        modified_By: currentUserId,
        modified_On: formatLocalDateTime(new Date()),
        details: details,
        resourceDetails: resourceDetails,
      };
      
      delete bookingData.booking_Id;

      const result = await saveBooking(bookingData);
      
      if (result.id && result.msg) {
        if (consentData.guestSignature || consentData.witnessSignature) {
          try {
            const consentPayload = {
              booking_Id: result.id,
              witnessName: consentData.witnessName?.trim() || null,
              witnessSignature: consentData.witnessSignature || null,
              guestSignature: consentData.guestSignature || null,
            };
            await signBookingConsent(consentPayload);
          } catch (error) {
            console.error('Error saving consent:', error);
          }
        }
        
        Swal.fire("Success", result.msg || result.message || "Booking saved successfully", "success");
        resetForm();
      } else {
        Swal.fire("Error", result.msg || result.message || "Failed to save booking", "error");
      }
    } catch (error) {
      console.error("Error saving booking:", error);
      Swal.fire("Error", "Failed to save booking", "error");
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setForm({
      booking_Id: null,
      customer_Id: null,
      employee_Id: null,
      room_Id: null,
      from_Time: new Date(),
      to_Time: new Date(),
      entry_Date: new Date(),
      booking_Date: new Date(),
      is_Cancelled: false,
      cancellation_Reason: "",
      notes: "",
      source_Id: null,
      sourceOther: "",
      modified_By: null,
      modified_On: null,
      selectedServices: [],
      selectedPackages: [],
      selectedPackageSeries: [],
    });
    setServiceResources([]);
    setSelectedCustomerOption(null);
    setFormKey(prev => prev + 1);
    
    setCustomerMembership(null);
    setAllCustomerMemberships([]);
    setHasMembership(false);
    setMembershipServices([]);
    setMembershipPackages([]);
    setMembershipPackageSeries([]);
    
    setShowMembershipModal(false);
    setSelectedMembershipServices([]);
    setSelectedMembershipPackages([]);
    setSelectedMembershipPackageSeries([]);
    setTableSelection({});
    
    setConsentData({
      witnessName: loggedInEmployeeName,
      witnessSignature: '',
      guestSignature: ''
    });
  };

  const handleSignConsent = async (consentPayload) => {
    try {
      setConsentLoading(true);
      setConsentData({
        witnessName: consentPayload.witnessName || '',
        witnessSignature: consentPayload.witnessSignature || '',
        guestSignature: consentPayload.guestSignature || ''
      });
      Swal.fire("Success", "Consent form signed. It will be saved when you save the booking.", "success");
    } catch (error) {
      console.error('Error signing consent:', error);
      Swal.fire("Error", "Failed to sign consent", "error");
    } finally {
      setConsentLoading(false);
    }
  };


  if (!hasPermission) {
    return null;
  }

  return (
    <div className="page-content">
      <Container fluid>
        <Row>
          <Col lg="12">
            <Breadcrumb title="New Booking" breadcrumbItem="Spa" />
            <Card>
              <CardBody>
                <h4 className="card-title mb-4">
                  <Calendar className="me-2" size={20} />
                  New Booking
                </h4>

                <Form onSubmit={handleSubmit}>
                  <Row>
                    <Col md="6">
                      <FormGroup className="mb-3">
                        <Label for="customer" className="mb-2">
                          Customer *
                          {hasMembership && (
                            <span className="badge bg-success ms-2">
                              <i className="mdi mdi-crown me-1"></i>
                              Membership
                            </span>
                          )}
                        </Label>
                        <div className="d-flex gap-2">
                          <div style={{ flex: 1 }}>
                            <Select
                              key={`customer-${formKey}`}
                              value={selectedCustomerOption}
                              onChange={(option) => {
                                setSelectedCustomerOption(option);
                                handleFormChange('customer_Id', option?.value);
                              }}
                              options={customerSearchOptions}
                              placeholder="Type at least 3 characters to search customers..."
                              styles={selectStyles}
                              isClearable
                              isSearchable
                              isLoading={customerSearchLoading}
                              noOptionsMessage={() => "Type at least 3 characters to search"}
                              loadingMessage={() => "Searching customers..."}
                              onInputChange={(inputValue) => {
                                if (searchTimeout) {
                                  clearTimeout(searchTimeout);
                                }
                                const timeout = setTimeout(() => {
                                  searchCustomersManually(inputValue);
                                }, 300);
                                setSearchTimeout(timeout);
                                return inputValue;
                              }}
                            />
                          </div>
                          {selectedCustomerOption?.value && (
                            <>
                              <Button
                                color="info"
                                size="sm"
                                onClick={() => setShowCustomerProfile(true)}
                                title="View Customer Profile"
                              >
                                <User size={16} />
                              </Button>
                              <Button
                                type="button"
                                color="warning"
                                size="sm"
                                onClick={() => setShowConsentModal(true)}
                                title="Sign Consent Form"
                              >
                                <i className="fas fa-signature me-1"></i>
                                Consent
                              </Button>
                            </>
                          )}
                          <Button
                            color="success"
                            size="sm"
                            onClick={() => setShowQuickAddCustomer(true)}
                            title="Quick Add Customer"
                          >
                            <i className="fas fa-plus"></i>
                          </Button>
                          {hasMembership && (
                            <Button
                              color="info"
                              size="sm"
                              onClick={openMembershipModal}
                              title="Select Membership Items"
                            >
                              <i className="mdi mdi-crown"></i>
                            </Button>
                          )}
                        </div>
                      </FormGroup>
                    </Col>
                    <Col md="6">
                      <FormGroup className="mb-3">
                        <Label for="bookingDate" className="mb-2">Booking Date *</Label>
                        <DatePicker
                          selected={form.booking_Date}
                          onChange={(date) => handleFormChange('booking_Date', date)}
                          className="form-control"
                          dateFormat="dd/MM/yyyy"
                          placeholderText="Select Date"
                        />
                      </FormGroup>
                    </Col>
                  </Row>

                  <Row>
                    <Col md="6">
                      <FormGroup className="mb-3">
                        <Label for="packages" className="mb-2">Packages</Label>
                        <Select
                          key={`packages-${formKey}`}
                          value={form.selectedPackages}
                          onChange={(options) => handleFormChange('selectedPackages', options || [])}
                          options={packages}
                          placeholder="Select Packages"
                          styles={selectStyles}
                          isMulti
                          isClearable
                          isSearchable
                          isDisabled={checkBookingServiceTypes().hasPackageService}
                        />
                        {checkBookingServiceTypes().hasPackageService && (
                          <small className="text-muted d-block mt-1">
                            <i className="mdi mdi-information-outline me-1"></i>
                            Disabled: Package membership services are already in this booking
                          </small>
                        )}
                      </FormGroup>
                    </Col>
                    <Col md="6">
                      <FormGroup className="mb-3">
                        <Label for="services" className="mb-2">Services</Label>
                        <Select
                          key={`services-${formKey}`}
                          value={form.selectedServices}
                          onChange={(options) => handleFormChange('selectedServices', options || [])}
                          options={services}
                          placeholder="Select Services"
                          styles={selectStyles}
                          isMulti
                          isClearable
                          isSearchable
                          isDisabled={checkBookingServiceTypes().hasPackageService}
                        />
                        {checkBookingServiceTypes().hasPackageService && (
                          <small className="text-muted d-block mt-1">
                            <i className="mdi mdi-information-outline me-1"></i>
                            Disabled: Package membership services are already in this booking
                          </small>
                        )}
                      </FormGroup>
                    </Col>
                  </Row>

                  {serviceResources.length > 0 && (
                    <Row>
                      <Col md="12">
                        <FormGroup className="mb-3">
                          <Label className="mb-2">Service Resource Allocation</Label>
                          <div className="border rounded p-3">
                            <Table responsive striped>
                              <thead>
                                <tr>
                                  <th style={{ width: '25%' }}>Service</th>
                                  <th style={{ width: '12%' }}>From Time</th>
                                  <th style={{ width: '12%' }}>To Time</th>
                                  <th style={{ width: '18%' }}>Employee</th>
                                  <th style={{ width: '15%' }}>Room</th>
                                  <th style={{ width: '18%' }}>Equipment</th>
                                  <th style={{ width: '5%', textAlign: 'center' }}>Remove</th>
                                </tr>
                              </thead>
                              <tbody>
                                {serviceResources.map((resource) => (
                                  <tr 
                                    key={resource.id}
                                    style={{
                                      backgroundColor: resource.packageId && !resource.membershipId ? '#f8f9fa' : 'transparent',
                                      borderLeft: resource.packageId && !resource.membershipId ? '3px solid #007bff' : 'none'
                                    }}
                                  >
                                    <td>
                                      <div>
                                        <strong>{resource.serviceName}</strong>
                                        {resource.packageName && (
                                          <div className="text-muted small">
                                            <i className="mdi mdi-package me-1"></i>
                                            Package: {resource.packageName}
                                          </div>
                                        )}
                                        {resource.membershipId && (
                                          <div className="text-info small">
                                            <i className="mdi mdi-crown me-1"></i>
                                            Membership
                                          </div>
                                        )}
                                      </div>
                                    </td>
                                    <td>
                                      <DatePicker
                                        selected={resource.fromTime}
                                        onChange={(date) => {
                                          const service = services.find(s => s.value === resource.serviceId);
                                          const duration = service?.duration || 30;
                                          const toTime = new Date(date.getTime() + (duration * 60000));
                                          
                                          // Update both times in state first
                                          setServiceResources(prev => 
                                            prev.map(r => 
                                              r.id === resource.id 
                                                ? { ...r, fromTime: date, toTime: toTime }
                                                : r
                                            )
                                          );
                                          
                                          // Then check availability with the new times
                                          if (date && toTime) {
                                            setCheckingTimeAvailability(true);
                                            setTimeout(() => {
                                              checkTimeAvailability(resource.id, date, toTime).finally(() => {
                                                setCheckingTimeAvailability(false);
                                                // Also trigger bulk availability check to refresh all dropdowns
                                                checkAvailabilityForSelection();
                                              });
                                            }, 500);
                                          }
                                        }}
                                        showTimeSelect
                                        showTimeSelectOnly
                                        timeIntervals={15}
                                        timeCaption="Time"
                                        dateFormat="h:mm aa"
                                        className="form-control"
                                        style={{ width: '100%' }}
                                      />
                                    </td>
                                    <td>
                                      <DatePicker
                                        selected={resource.toTime}
                                        onChange={(date) => {
                                          // Update toTime in state
                                          setServiceResources(prev => 
                                            prev.map(r => 
                                              r.id === resource.id 
                                                ? { ...r, toTime: date }
                                                : r
                                            )
                                          );
                                          
                                          // Then check availability with the updated times
                                          const updatedResource = { ...resource, toTime: date };
                                          if (updatedResource.fromTime && updatedResource.toTime) {
                                            setCheckingTimeAvailability(true);
                                            setTimeout(() => {
                                              checkTimeAvailability(resource.id, updatedResource.fromTime, updatedResource.toTime).finally(() => {
                                                setCheckingTimeAvailability(false);
                                                // Also trigger bulk availability check to refresh all dropdowns
                                                checkAvailabilityForSelection();
                                              });
                                            }, 500);
                                          }
                                        }}
                                        showTimeSelect
                                        showTimeSelectOnly
                                        timeIntervals={15}
                                        timeCaption="Time"
                                        dateFormat="h:mm aa"
                                        className="form-control"
                                        style={{ width: '100%' }}
                                      />
                                    </td>
                                    <td>
                                      <Select
                                        key={`employee-${resource.id}-${availabilityRefreshKey}`}
                                        value={employees.find(emp => emp.value === resource.employeeId)}
                                        onChange={(option) => {
                                          const timeAvailability = timeAvailabilityResults[resource.id];
                                          if (timeAvailability && option?.value) {
                                            const employee = timeAvailability.availableEmployees?.find(emp => emp.employee_Id === option.value);
                                            if (employee && !employee.isAvailable) {
                                              Swal.fire("Warning", "This employee is not available for the selected time slot", "warning");
                                              return;
                                            }
                                          }
                                          handleResourceChange(resource.id, 'employeeId', option?.value);
                                        }}
                                        options={formatOptionsWithAvailability(employees, 'Employee', resource.id)}
                                        placeholder={checkingTimeAvailability ? "Checking availability..." : "Select"}
                                        styles={selectStyles}
                                        isClearable
                                        isSearchable
                                        isLoading={checkingTimeAvailability}
                                      />
                                    </td>
                                    <td>
                                      <Select
                                        key={`room-${resource.id}-${availabilityRefreshKey}`}
                                        value={rooms.find(room => room.value === resource.roomId)}
                                        onChange={(option) => {
                                          const timeAvailability = timeAvailabilityResults[resource.id];
                                          if (timeAvailability && option?.value) {
                                            const room = timeAvailability.availableRooms?.find(room => room.room_Id === option.value);
                                            if (room && !room.isAvailable) {
                                              Swal.fire("Warning", "This room is not available for the selected time slot", "warning");
                                              return;
                                            }
                                          }
                                          handleResourceChange(resource.id, 'roomId', option?.value);
                                        }}
                                        options={formatOptionsWithAvailability(rooms, 'Room', resource.id)}
                                        placeholder={checkingTimeAvailability ? "Checking availability..." : "Select"}
                                        styles={selectStyles}
                                        isClearable
                                        isSearchable
                                        isLoading={checkingTimeAvailability}
                                      />
                                    </td>
                                    <td>
                                      <Select
                                        value={equipments.find(eq => eq.value === resource.equipmentId)}
                                        onChange={(option) => handleResourceChange(resource.id, 'equipmentId', option?.value)}
                                        options={equipments}
                                        placeholder="Select"
                                        styles={selectStyles}
                                        isClearable
                                        isSearchable
                                      />
                                    </td>
                                    <td style={{ textAlign: 'center', verticalAlign: 'middle' }}>
                                      <Button
                                        color="danger"
                                        size="sm"
                                        onClick={() => handleRemoveService(resource.id)}
                                        style={{ fontSize: '11px', padding: '4px 8px' }}
                                        title="Remove Service"
                                      >
                                        <X size={14} />
                                      </Button>
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </Table>
                          </div>
                        </FormGroup>
                      </Col>
                    </Row>
                  )}

                  <Row>
                    <Col md="6">
                      <FormGroup className="mb-3">
                        <Label for="source" className="mb-2">Source *</Label>
                        <Select
                          key={`source-${formKey}`}
                          value={sourceOptions.find(s => s.value === form.source_Id)}
                          onChange={(option) => handleFormChange('source_Id', option?.value)}
                          options={sourceOptions}
                          placeholder={sourcesLoading ? "Loading sources..." : "Select Source"}
                          styles={selectStyles}
                          isClearable
                          isSearchable
                          isLoading={sourcesLoading}
                          noOptionsMessage={() => sourcesLoading ? "Loading..." : "No sources found"}
                        />
                      </FormGroup>
                    </Col>
                  </Row>

                  <Row>
                    <Col md="12">
                      <FormGroup className="mb-4">
                        <Label for="notes" className="mb-2">Notes</Label>
                        <Input
                          type="textarea"
                          id="notes"
                          value={form.notes}
                          onChange={(e) => handleFormChange('notes', e.target.value)}
                          rows="3"
                          placeholder="Enter any additional notes..."
                        />
                      </FormGroup>
                    </Col>
                  </Row>

                  <Row>
                    <Col md="12">
                      <div className="d-flex gap-2">
                        <Button
                          type="submit"
                          color="primary"
                          disabled={loading}
                        >
                          {loading ? (
                            <ClipLoader size={16} color="#fff" />
                          ) : (
                            "Save Booking"
                          )}
                        </Button>
                        <Button
                          type="button"
                          color="secondary"
                          onClick={resetForm}
                        >
                          Clear
                        </Button>
                      </div>
                    </Col>
                  </Row>
                </Form>
              </CardBody>
            </Card>
          </Col>
        </Row>
      </Container>

      {/* Membership Selection Modal */}
      <Modal isOpen={showMembershipModal} toggle={closeMembershipModal} size="xl">
        <ModalHeader toggle={closeMembershipModal}>
          <i className="mdi mdi-crown me-2"></i>
          Select Membership Services
        </ModalHeader>
        <ModalBody>
          <div className="mb-3">
            <p className="text-muted">
              Select services from your membership. Only services with pending completion status are shown.
              Products are not displayed in this selection.
            </p>
          </div>
          
          {(() => {
            const tableData = buildMembershipTableData();
            return tableData.length > 0 ? (
              <div style={{ maxHeight: '500px', overflowY: 'auto' }}>
                <Table responsive striped hover className="mb-0">
                  <thead className="table-light" style={{ position: 'sticky', top: 0, zIndex: 10 }}>
                    <tr>
                      <th width="5%" style={{ textAlign: 'center' }}>Select</th>
                      <th width="30%">Service Name</th>
                      <th width="25%">Package Name</th>
                      <th width="15%" style={{ textAlign: 'center' }}>Available Qty</th>
                      <th width="20%" style={{ textAlign: 'center' }}>Required Qty</th>
                    </tr>
                  </thead>
                  <tbody>
                    {tableData.map((service) => (
                      <tr key={service.selectionKey}>
                        <td style={{ textAlign: 'center', verticalAlign: 'middle' }}>
                          <div className="form-check">
                            <input
                              className="form-check-input"
                              type="checkbox"
                              id={`service-table-${service.selectionKey}`}
                              checked={tableSelection[service.selectionKey]?.selected || false}
                              onChange={(e) => handleTableServiceSelection(service.selectionKey, e.target.checked)}
                            />
                          </div>
                        </td>
                        <td style={{ verticalAlign: 'middle' }}>
                          <i className="mdi mdi-spa me-2 text-primary"></i>
                          <strong>{service.serviceName}</strong>
                        </td>
                        <td style={{ verticalAlign: 'middle' }}>
                          <span className="text-muted">{service.packageNames}</span>
                        </td>
                        <td style={{ textAlign: 'center', verticalAlign: 'middle' }}>
                          <span className="badge bg-info" style={{ fontSize: '13px', padding: '6px 12px' }}>
                            {service.availableQty}
                          </span>
                        </td>
                        <td style={{ textAlign: 'center', verticalAlign: 'middle' }}>
                          {tableSelection[service.selectionKey]?.selected ? (
                            <Input
                              type="number"
                              min="1"
                              max={service.availableQty}
                              value={tableSelection[service.selectionKey]?.requiredQty !== undefined && tableSelection[service.selectionKey]?.requiredQty !== '' 
                                ? tableSelection[service.selectionKey].requiredQty 
                                : ''}
                              onChange={(e) => handleRequiredQtyChange(service.selectionKey, e.target.value)}
                              onBlur={(e) => {
                                // If empty on blur, set to 1
                                if (!e.target.value || e.target.value === '') {
                                  handleRequiredQtyChange(service.selectionKey, '1');
                                }
                              }}
                              style={{ width: '80px', textAlign: 'center' }}
                              className="d-inline-block"
                              placeholder="1"
                            />
                          ) : (
                            <span className="text-muted">-</span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </div>
            ) : (
              <div className="text-center text-muted py-4">
                <i className="mdi mdi-information-outline me-2"></i>
                No pending membership services available for selection.
              </div>
            );
          })()}
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" onClick={closeMembershipModal}>
            Cancel
          </Button>
          <Button color="primary" onClick={applyMembershipSelections}>
            Apply Selections
          </Button>
        </ModalFooter>
      </Modal>

      {/* Quick Add Customer Modal */}
      <QuickAddCustomer
        isOpen={showQuickAddCustomer}
        toggle={() => setShowQuickAddCustomer(false)}
        onCustomerAdded={handleCustomerAdded}
      />

      {/* Customer Profile Modal */}
      <CustomerProfileModal
        isOpen={showCustomerProfile}
        toggle={() => setShowCustomerProfile(false)}
        customerId={selectedCustomerOption?.value || null}
        customer={selectedCustomerOption ? { customer_Id: selectedCustomerOption.value } : null}
      />

      {/* Consent Form Modal */}
      {selectedCustomerOption?.value && (
        <ConsentFormModal
          isOpen={showConsentModal}
          toggle={() => setShowConsentModal(false)}
          bookingId={0}
          customerName={selectedCustomerOption?.customer?.customer_NameEn || (selectedCustomerOption?.label ? selectedCustomerOption.label.split(' — ')[0] : '') || ''}
          customerMobile={selectedCustomerOption?.customer?.customer_ContactNo || (selectedCustomerOption?.label ? selectedCustomerOption.label.split(' — ')[1] : '') || ''}
          isConsentSigned={false}
          witnessName={consentData.witnessName || loggedInEmployeeName}
          witnessSignature={consentData.witnessSignature}
          guestSignature={consentData.guestSignature}
          onSignConsent={handleSignConsent}
        />
      )}
    </div>
  );
};

export default AddBookingPage;